import { Tooltip, Dropdown, MenuProps } from "antd";
import { RightOutlined } from "@ant-design/icons";
import { PanelLeft, PanelLeftDashed, EllipsisVertical, BookOpen, GamepadDirectional, BookPlus } from "lucide-react";
import cls from "classnames";
import { useAtom, useAtomValue } from "jotai";
import { useMutation } from "@tanstack/react-query";
import { isJiajiaClassApp } from "@/utils/app";
import { eventEmitterSingleton } from "@/utils/eventEmitterSingleton";
import styles from "./index.module.scss";
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import useRouteQuery from "@/hooks/useRouteQuery";

import {
  isWorkLeftPanelOpenAtom,
  worksAtom,
  recentLessonAtom,
  generateTasksAtom,
} from "@/atom/drawBoard";
import { userInfoAtom } from "@/atom/user";
import BtnAdd from "@/components/BtnAdd";
import WorkItem from "@/components/WorkItem";
import GenerateTaskItem from "@/components/GenerateTaskItem";
import {
  getControlPanelSettings,
  updateCourseStart,
} from "@/services/Course/courseApi";
import { fetchPublishShareWork, postDownlineShareWork } from "@/services/share";
import useUpdateCourseSettings from "@/hooks/useUpdateCourseSettings";
import useWork from "@/hooks/useWork";

const ControlPanel = () => {
  const { lessonId = "", shareId = "", taskId = "", workId = "" } = useRouteQuery();
  const works = useAtomValue(worksAtom);
  let navigate = useNavigate();
  const [isWorkLeftPanelOpen, setIsWorkLeftPanelOpen] = useAtom(
    isWorkLeftPanelOpenAtom
  );
  const { updateLeftPanelStatus } = useUpdateCourseSettings();
  const userInfo = useAtomValue(userInfoAtom);
  const [recentLesson, setRecentLesson] = useAtom(recentLessonAtom);
  const [generateTasks, setGenerateTasks] = useAtom(generateTasksAtom);
  const [hasCourse, setHasCourse] = useState<boolean>(false);
  const [shareWorkList, setShareWorkList] = useState<any[]>();

  // 添加用于跟踪每个recentLesson项目是否需要tooltip的状态
  const [showTooltipMap, setShowTooltipMap] = useState<Record<number, boolean>>(
    {}
  );
  const titleRefs = useRef<Record<number, HTMLParagraphElement | null>>({});

  const isMobile = isJiajiaClassApp;

  const { setWorksList } = useWork();

  // useEffect(() => {
  //   async function getSettings() {
  //     let res = await getWorkPanelSettings();
  //     if (res?.code === 0 && res?.msg === "success") {
  //       setWorksList(res.data.works);
  //     }
  //   }
  //   getSettings();
  // }, []);

  useEffect(() => {
    if (!userInfo?.userId) return;

    async function getData() {
      let [res, resShareWork] = await Promise.all([
        getControlPanelSettings(),
        fetchPublishShareWork(),
      ]);
      if (res.code === 0 && res.msg === "success") {
        setWorksList(res.data.works);
        setRecentLesson(() => res.data.recentLesson);
        setHasCourse(res.data.hasCourse);

        // 新增：处理 generateTasks
        if (res.data.generateTasks && Array.isArray(res.data.generateTasks)) {
          setGenerateTasks(res.data.generateTasks);
        }
      }
      if (res.code === 0 && res.msg === "success") {
        setShareWorkList(resShareWork.data);
      }
    }

    getData();
  }, [userInfo?.userId]);

  const handleMenuClick: MenuProps["onClick"] = async (e) => {
    navigate(`/${e.key}`);
  };

  const menu: MenuProps = {
    items: [
      ...(!isJiajiaClassApp
        ? [
            {
              key: "vipshow",
              label: <span style={{ fontSize: 16 }}>订阅火箭爱学</span>,
            },
          ]
        : []),
      {
        key: "personalinfo",
        label: <span style={{ fontSize: 16 }}>个人信息</span>,
      },
      {
        key: "audiocheck",
        label: <span style={{ fontSize: 16 }}>语音测试</span>,
      },
      ...(!isJiajiaClassApp
        ? [
            {
              key: "download",
              label: <span style={{ fontSize: 16 }}>下载软件</span>,
            },
          ]
        : []),
    ],
    onClick: handleMenuClick,
  };

  const goToLesson = async (lesson: any) => {
    if (!userInfo?.userId) {
      let loc = `/enter?preLocation=/courseinfo?courseId=${lesson.id}`;

      navigate(loc);
      return;
    } else {
      if (lesson.status !== 0 && lesson.status !== 3) {
        await updateCourseStart(lesson.id);
      }

      if (lesson?.status === 3) {
        navigate(`/courseinfo?courseId=${lesson?.courseId}`);
        return;
      }
      if (lesson.seriesId === 6) {
        // setIsWorkLeftPanelOpen(false);
        let url = `/course?lessonId=${lesson.id}`;
        navigate(url, {
          state: {
            lessonData: lesson,
            source: 'controlPanel'
          }
        });
        return;
      } else if (lesson.seriesId === 14) {
        //数学
        let url = `/course?lessonId=${lesson.id}&type=m`;
        navigate(url, {
          state: {
            lessonData: lesson,
            source: 'controlPanel'
          }
        });
        return;
      } else if (
        lesson.seriesId === 13 ||
        lesson.seriesId === 16 ||
        lesson.seriesId === 17
      ) {
        //数学
        let url = `/course?lessonId=${lesson.id}&type=m&courseType=s`;
        navigate(url, {
          state: {
            lessonData: lesson,
            source: 'controlPanel'
          }
        });
        return;
      } else if (lesson.seriesId === 4) {
        //时文精选
        let url = `/course?lessonId=${lesson.id}&type=m&courseType=english`;
        navigate(url, {
          state: {
            lessonData: lesson,
            source: 'controlPanel'
          }
        });
        return;
      } else if (lesson.seriesId === 15) {
        //自然拼读
        let url = `/course?lessonId=${lesson.id}&type=er&courseType=english`;
        navigate(url, {
          state: {
            lessonData: lesson,
            source: 'controlPanel'
          }
        });
        return;
      } else {
        let url = `/teach/course/${lesson.courseAlias}`;
        if (lesson.status === 2 || lesson.status === 4) {
          url = `/teach/reviewcourse/${lesson.courseAlias}?lessonId=${lesson.id}&courseId=${lesson.courseId}`;
        }
        // window.open(url);
        window.location.href = url;
      }
    }
  };

  const mutationPostDownlineShareWork = useMutation({
    mutationFn: postDownlineShareWork,
    onSuccess: (resData, params) => {
      const { code } = resData;
      if (code === 0) {
        setShareWorkList((prev) => {
          const newList = prev?.filter(
            (item) => item.shareId !== params.shareId
          );
          if (params.shareId === shareId) {
            if (newList?.length === 0) {
              navigate(
                `/work?workId=${
                  works?.[0]?.workId ||
                  generateTasks?.[0]?.generateId
                }`
              );
            } else {
              navigate(`/shareWork?shareId=${shareWorkList?.[0]?.shareId}`);
            }
          }
          return newList;
        });
      }
    },
  });

  const deleteShareApp = (shareId: any) => {
    mutationPostDownlineShareWork.mutate({
      shareId: shareId,
      action: "delete",
    });
  };

  // 键盘导航处理函数
  const handleKeyDown = (
    e: React.KeyboardEvent,
    action: () => void
  ) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      action();
    }
  };

  const isMyCoursesActive = window.location.pathname.toLowerCase() === "/mycourses";
  const isAllCoursesActive = window.location.pathname.includes("allcourses");

  return (
    <div className={styles.container} role="complementary" aria-label="控制面板">
      <nav className={styles.headerWrapper}>
        <div
          className={cls(styles.logo, {
            [styles.logoHidden]: !isWorkLeftPanelOpen
          })}
          onClick={() => {
            window.location.href = `/`;
          }}
          role="button"
          tabIndex={0}
          aria-label="返回首页"
          onKeyDown={(e) => handleKeyDown(e, () => {
            window.location.href = `/`;
          })}
        ></div>
        {isWorkLeftPanelOpen && (
          <Tooltip
            placement="bottom"
            title="收起侧栏"
          >
            <button
              className={styles.leftPanelSwitch}
              onClick={() => {
                setIsWorkLeftPanelOpen(false);
                updateLeftPanelStatus(false);
              }}
              aria-label="收起侧栏"
              aria-pressed={false}
            >
              <PanelLeft className={styles.icon} />
            </button>
          </Tooltip>
        )}
      </nav>

      <div className={styles.contentWrapper}>
        <div style={{ padding: "0 10px" }}>
          <BtnAdd isLeftPanel={true} />
        </div>

        <nav className={styles.workList} aria-label="主导航">
          {hasCourse && (
            <div
              className={cls({
                [styles.categoryBlock]: true,
                [styles.selected]: isMyCoursesActive,
              })}
              onClick={() => {
                navigate("/myCourses");
              }}
              role="button"
              tabIndex={0}
              aria-label="我的课程"
              aria-current={isMyCoursesActive ? "page" : undefined}
              onKeyDown={(e) => handleKeyDown(e, () => navigate("/myCourses"))}
            >
              <BookPlus className={styles.myCoursesIcon} aria-hidden="true" />
              <p className={styles.category}>我的课程</p>
              <RightOutlined className={styles.rightIcon} aria-hidden="true" />
            </div>
          )}

          <div
            className={cls({
              [styles.categoryBlock]: true,
              [styles.selected]: isAllCoursesActive,
            })}
            onClick={() => {
              navigate("/allcourses");
            }}
            role="button"
            tabIndex={0}
            aria-label="发现课程"
            aria-current={isAllCoursesActive ? "page" : undefined}
            onKeyDown={(e) => handleKeyDown(e, () => navigate("/allcourses"))}
          >
            <GamepadDirectional className={styles.allCoursesIcon} aria-hidden="true" />
            <p className={styles.category}>发现课程</p>
            <RightOutlined className={styles.rightIcon} aria-hidden="true" />
          </div>

          <section className={styles.workBlock} aria-label="最近课程">
            <p className={styles.title}>在学</p>
            {recentLesson.slice(0, 5).map((item: any, index: number) => {
              const isSelected = lessonId === item?.id?.toString();

              // 创建ref处理函数
              const setTitleRef = (el: HTMLParagraphElement | null) => {
                if (el) {
                  titleRefs.current[index] = el;
                  // 检查文本是否溢出
                  const { scrollWidth, clientWidth } = el;
                  setShowTooltipMap((prev) => ({
                    ...prev,
                    [index]: scrollWidth > clientWidth && !isMobile,
                  }));
                }
              };

              return (
                <div
                  key={index.toString()}
                  className={cls({
                    [styles.categoryBlock]: true,
                    [styles.selected]: isSelected,
                  })}
                  onClick={() => goToLesson(item)}
                  role="button"
                  tabIndex={0}
                  aria-label={`课程：${item.title}`}
                  aria-current={isSelected ? "page" : undefined}
                  onKeyDown={(e) => handleKeyDown(e, () => goToLesson(item))}
                >
                  <BookOpen className={styles.recentLessonIcon} aria-hidden="true" />
                  <Tooltip
                    title={showTooltipMap[index] ? item.title : ""}
                    placement="top"
                  >
                    <p ref={setTitleRef} className={styles.category}>
                      {item.title}
                    </p>
                  </Tooltip>
                </div>
              );
            })}
          </section>

          {generateTasks && generateTasks.length > 0 && (
            <section className={styles.workBlock} aria-label="生成任务">
              <p className={styles.title}>在做</p>
              {generateTasks.map((task, index: number) => {
                const isSelected = taskId === task?.generateId?.toString();

                return (
                  <GenerateTaskItem
                    key={task.generateId}
                    task={task}
                    isSelected={isSelected}
                    onTaskClick={() => {
                      // 发射切换到project视图的事件
                      const eventInstance = eventEmitterSingleton.getInstance();
                      eventInstance.emit("switch_to_project_view");

                      navigate(`/work?taskId=${task.generateId}`, {
                        state: {
                          taskData: task,
                          source: 'controlPanel'
                        }
                      });
                    }}
                  />
                );
              })}
            </section>
          )}

          {shareWorkList && shareWorkList?.length > 0 && (
            <section className={styles.workBlock} aria-label="应用列表">
              <p className={styles.title}>应用</p>
              {shareWorkList.map((item: any, index: number) => {
                return (
                  <WorkItem
                    key={index}
                    item={item}
                    type="shareApp"
                    onDelete={() => deleteShareApp(item.shareId)}
                  ></WorkItem>
                );
              })}
            </section>
          )}

          {works && works.length > 0 && (
            <section className={styles.workBlock} aria-label="在问作品">
              <p className={styles.title}>在问</p>
              {works.map((item: any, index: number) => {
                return <WorkItem key={index} item={item}></WorkItem>;
              })}
            </section>
          )}
        </nav>
      </div>

      <Dropdown
        trigger={["click"]}
        menu={menu}
        placement="bottomRight"
        align={{
          offset: [65, -7], // 第一个值控制水平偏移，第二个值控制垂直偏移
        }}
        overlayClassName={styles.dropDownMenu}
      >
        <div
          className={styles.avatarBlock}
          role="button"
          tabIndex={0}
          aria-label={`用户菜单：${userInfo?.nickname}`}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              // 触发下拉菜单点击
            }
          }}
        >
          <img
            src={userInfo?.avatar}
            className={styles.avatar}
            alt={userInfo?.nickname}
          />
          <p className={styles.category}>{userInfo?.nickname}</p>
          <div className={styles.dropDown}>
            <EllipsisVertical className={styles.workSetting} aria-hidden="true" />
          </div>
        </div>
      </Dropdown>
    </div>
  );
};

export default ControlPanel;
